import React, { useState, useEffect } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useLocation } from "react-router-dom";
import ChatBox from "../components/ChatBox";
import Sidebar, { getLastVisitedThread } from "../components/Sidebar";
import { openCharacter, getUserOpenedCharacters } from "../utils/characterUtils";
import { getSubscriptionStatus, canAccessChat } from "../utils/subscriptionUtils";
import { getUserId } from "../utils/userUtils";

const ChatInterface = ({ chatBotId: initialChatBotId = "0" }) => {
  const { user, signOut } = useAuthenticator((context) => [
    context.user,
    context.signOut,
  ]);
  
  const location = useLocation();
  const [selectedChatBotId, setSelectedChatBotId] = useState(initialChatBotId);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [userOpenedCharacters, setUserOpenedCharacters] = useState([]);
  const [loading, setLoading] = useState(true);

  // Parse bot_id from URL query parameters for custom characters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const botIdFromUrl = urlParams.get('bot_id');
    
    if (botIdFromUrl) {
      console.log('Using bot_id from URL:', botIdFromUrl);
      setSelectedChatBotId(botIdFromUrl);
    }
  }, [location.search]);

  // Under unified subscription model, all characters are available
  // Subscription only affects message sending, not character visibility
  const subscribedProducts = ["0", "1"]; // Built-in characters always available

  // Load user data and subscription status when component mounts
  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        try {
          setLoading(true);
          const userId = getUserId(user);
          
          // Load user's opened characters for sidebar
          const openedCharacters = await getUserOpenedCharacters(userId);
          setUserOpenedCharacters(openedCharacters);
          
          // Load subscription status for current character
          const status = await getSubscriptionStatus(user, selectedChatBotId);
          setSubscriptionStatus(status);
          
          // Load last visited thread if no specific character is selected
          if (!location.search.includes('bot_id')) {
            const lastVisited = getLastVisitedThread();
            if (lastVisited && lastVisited.botId && lastVisited.threadId) {
              console.log(
                `Loading last visited thread: botId=${lastVisited.botId}, threadId=${lastVisited.threadId}`
              );
              setSelectedChatBotId(lastVisited.botId);
              setSelectedThreadId(lastVisited.threadId);
            }
          }
        } catch (error) {
          console.error('Error loading user data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserData();
  }, [user, selectedChatBotId, location.search]);

  // Track character opening when user accesses a character chat
  useEffect(() => {
    const trackCharacterOpen = async () => {
      if (user && selectedChatBotId) {
        try {
          const userId = getUserId(user);
          console.log('Tracking character open:', { userId, characterId: selectedChatBotId });
          
          // Track that user opened this character
          await openCharacter(userId, selectedChatBotId);
          
          // Refresh opened characters list
          const openedCharacters = await getUserOpenedCharacters(userId);
          setUserOpenedCharacters(openedCharacters);
        } catch (error) {
          console.error('Error tracking character open:', error);
        }
      }
    };

    trackCharacterOpen();
  }, [user, selectedChatBotId]);

  // Update subscription status when character changes
  useEffect(() => {
    const updateSubscriptionStatus = async () => {
      if (user && selectedChatBotId) {
        try {
          const status = await getSubscriptionStatus(user, selectedChatBotId);
          setSubscriptionStatus(status);
        } catch (error) {
          console.error('Error updating subscription status:', error);
        }
      }
    };

    updateSubscriptionStatus();
  }, [user, selectedChatBotId]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900 text-white">
        <p>Loading...</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900 text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Loading chat interface...</p>
        </div>
      </div>
    );
  }

  const handleChatBotSelection = (botId, threadId = null) => {
    setSelectedChatBotId(botId);
    setSelectedThreadId(threadId);
  };

  return (
    <div className="flex h-screen bg-gray-900 text-white">
      <Sidebar
        user={user}
        subscribedProducts={subscribedProducts}
        userOpenedCharacters={userOpenedCharacters}
        setSelectedChatBotId={handleChatBotSelection}
      />
      <div className="flex-1 overflow-hidden">
        {subscriptionStatus && !subscriptionStatus.canAccessChat ? (
          // Show access denied message for non-logged users
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <h2 className="text-2xl font-bold mb-4">Access Required</h2>
              <p className="text-gray-300 mb-6">{subscriptionStatus.message}</p>
              <a
                href="/controlpanel"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Login to Continue
              </a>
            </div>
          </div>
        ) : (
          <ChatBox 
            chatBotId={selectedChatBotId} 
            threadId={selectedThreadId}
            subscriptionStatus={subscriptionStatus}
          />
        )}
      </div>
    </div>
  );
};

export default ChatInterface;
